// What the save panel shows while the open document is saved: its upload, and for an encrypted
// document in Office for the web the steps of saving its stored copy (FileService.getStoredCopy) with
// the user's answers going back to that save. A module-level store rather than redux: the save runs
// deep in services, outside React.

export enum StoredCopyStep {
  Idle = "idle",
  // The stored copy may miss the last edits: save it anyway, or wait for Office to store them.
  Choice = "choice",
  WaitingForChanges = "waitingForChanges",
  Downloading = "downloading",
  Uploading = "uploading"
}

export enum StoredCopyChoice {
  SaveStoredCopy = "saveStoredCopy",
  WaitForChanges = "waitForChanges",
  Cancel = "cancel"
}

export interface IStoredCopySaveState {
  step: StoredCopyStep;
  storedAt?: Date;
  transferred?: number;
  total?: number;
  // "Saving is taking longer than expected"
  isSlow: boolean;
}

// Measured from the start of the download; the user's choice doesn't count.
export const SLOW_AFTER_MS = 60_000;
export const STALLED_AFTER_MS = 20_000;
const CHECK_EVERY_MS = 1_000;

const IDLE: IStoredCopySaveState = { step: StoredCopyStep.Idle, isSlow: false };

class StoredCopySave {
  private state: IStoredCopySaveState = IDLE;
  private readonly listeners = new Set<() => void>();
  private controller: AbortController | null = null;
  private pendingAnswer: ((choice: StoredCopyChoice) => void) | null = null;
  private watch: ReturnType<typeof setInterval> | null = null;
  private transferStartedAt = 0;
  private lastProgressAt = 0;
  private keptWaitingAt = 0;

  getState = (): IStoredCopySaveState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  // Starts a save; its signal aborts when the user cancels.
  begin = (): AbortSignal => {
    this.controller?.abort();
    this.end();
    this.controller = new AbortController();
    return this.controller.signal;
  };

  // Starts a save unless one is under way: the stored copy's save began before the upload. Older
  // desktop Office shows the pane in Internet Explorer's web view, which has no AbortController: its
  // saves show no progress.
  ensureBegun = (): void => {
    if (!this.controller && typeof AbortController !== "undefined") {
      this.begin();
    }
  };

  ask = (step: StoredCopyStep.Choice | StoredCopyStep.WaitingForChanges, storedAt: Date): Promise<StoredCopyChoice> => {
    this.update({ step, storedAt, isSlow: false });
    return new Promise(resolve => { this.pendingAnswer = resolve; });
  };

  // Ignored unless a save began, so other uploads can report through it unconditionally.
  progress = (step: StoredCopyStep.Downloading | StoredCopyStep.Uploading, transferred: number, total: number): void => {
    if (!this.controller) {
      return;
    }

    const now = Date.now();
    if (!this.watch) {
      this.transferStartedAt = now;
      this.lastProgressAt = now;
      this.watch = setInterval(this.checkSlow, CHECK_EVERY_MS);
    }
    // Any change counts: a retried upload starts over from 0.
    if (step !== this.state.step || transferred !== this.state.transferred) {
      this.lastProgressAt = now;
    }
    this.pendingAnswer = null;
    this.update({ step, transferred, total, isSlow: this.state.isSlow });
  };

  // The upload's progress in the document's size: the request carries it as base64, a third larger.
  uploadProgress = (documentBytes: number) => ({ loaded, total }: { loaded: number; total?: number }): void =>
    this.progress(StoredCopyStep.Uploading, total ? loaded / total * documentBytes : 0, documentBytes);

  // Right before the upload: from then on the save can't be cancelled, so the panel shows the upload
  // (without Cancel) at once. False when the user cancelled before it.
  startUpload = (documentBytes: number): boolean => {
    if (this.controller?.signal.aborted) {
      return false;
    }
    this.progress(StoredCopyStep.Uploading, 0, documentBytes);
    return true;
  };

  // Escape at the question: cancels the save, like its Cancel. True when it did.
  cancelQuestion = (): boolean => {
    if (this.state.step !== StoredCopyStep.Choice && this.state.step !== StoredCopyStep.WaitingForChanges) {
      return false;
    }
    this.cancel();
    return true;
  };

  answer = (choice: StoredCopyChoice): void => {
    const resolve = this.pendingAnswer;
    this.pendingAnswer = null;
    resolve?.(choice);
  };

  keepWaiting = (): void => {
    this.keptWaitingAt = Date.now();
    this.update({ ...this.state, isSlow: false });
  };

  cancel = (): void => {
    this.controller?.abort();
    this.answer(StoredCopyChoice.Cancel);
  };

  end = (): void => {
    if (this.watch) {
      clearInterval(this.watch);
      this.watch = null;
    }
    this.answer(StoredCopyChoice.Cancel);
    this.controller = null;
    this.keptWaitingAt = 0;
    if (this.state !== IDLE) {
      this.update(IDLE);
    }
  };

  // Once shown, the message stays until the user answers it; after Keep waiting only a new stall
  // brings it back.
  private checkSlow = (): void => {
    if (this.state.isSlow || (this.state.step !== StoredCopyStep.Downloading && this.state.step !== StoredCopyStep.Uploading)) {
      return;
    }
    const now = Date.now();
    // Once uploaded, the API stores the file ("Finishing..."), which reports no progress: not a stall.
    const isUploaded = this.state.step === StoredCopyStep.Uploading && this.state.total > 0 && this.state.transferred >= this.state.total;
    const isStalled = !isUploaded && now - Math.max(this.lastProgressAt, this.keptWaitingAt) >= STALLED_AFTER_MS;
    const isTooLong = this.keptWaitingAt === 0 && now - this.transferStartedAt >= SLOW_AFTER_MS;
    if (isStalled || isTooLong) {
      this.update({ ...this.state, isSlow: true });
    }
  };

  private update = (state: IStoredCopySaveState): void => {
    this.state = state;
    this.listeners.forEach(listener => listener());
  };
}

export const storedCopySave = new StoredCopySave();
